import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const images = [
  {
    src: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b",
    alt: "Yoga Practice",
  },
  {
    src: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b",
    alt: "Holistic Nutrition",
  },
  {
    src: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4",
    alt: "Mental Wellness",
  },
  {
    src: "https://images.unsplash.com/photo-1588286840104-8957b019727f",
    alt: "Active Lifestyle",
  },
];

const Gallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.95,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex(
      (prevIndex) => (prevIndex + newDirection + images.length) % images.length
    );
  };

  return (
    <section
      id="gallery"
      className="py-32 bg-stone-900 text-stone-50 overflow-hidden relative"
    >
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay pointer-events-none" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl"
          >
            <span className="text-xs font-bold tracking-[0.2em] text-stone-400 uppercase mb-4 block">
              Visual Journey
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold mb-4 leading-tight">
              Experience Wellness
            </h2>
            <p className="text-stone-400 font-light text-lg">
              A glimpse into the tranquil world of healing and balance.
            </p>
          </motion.div>

          <div className="flex gap-4 mt-8 md:mt-0">
            <button
              className="p-4 rounded-full border border-white/10 hover:bg-white/10 transition-colors"
              onClick={() => paginate(-1)}
              aria-label="Previous slide"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              className="p-4 rounded-full border border-white/10 hover:bg-white/10 transition-colors"
              onClick={() => paginate(1)}
              aria-label="Next slide"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="relative h-[500px] md:h-[700px] w-full rounded-[2rem] overflow-hidden bg-stone-800 shadow-2xl">
          <AnimatePresence initial={false} custom={direction}>
            <motion.img
              key={currentIndex}
              src={images[currentIndex]?.src}
              alt={images[currentIndex]?.alt}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.4 },
                scale: { duration: 0.4 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="absolute w-full h-full object-cover"
            />
          </AnimatePresence>

          <div className="absolute bottom-8 left-8 bg-black/30 backdrop-blur-md px-6 py-3 rounded-full border border-white/10">
            <p className="text-sm font-medium tracking-wide">
              {images[currentIndex]?.alt}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
